import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Eye, Pencil, Trash2, Lock, ChevronDown } from 'lucide-react';
import ReactDOM from 'react-dom';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    fetchUsers as apiFetchUsers,
    createUser as apiCreateUser,
    updateUser as apiUpdateUser,
    updateUserStatus as apiUpdateUserStatus,
    updateUserPassword as apiUpdateUserPassword,
    deleteUser,
    type AdminUser as ApiAdminUser,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Preloader from '../../components/Preloder';
import { logger } from '../../utils/logger';

// Pagination helper functions (same as Reports page)
type PageButton = { key: string; page?: number; label: string | number; active?: boolean; disabled?: boolean };
function getPageNumbers(current: number, total: number): PageButton[] {
    const buttons: PageButton[] = [];
    const pushPage = (p: number, active = false) => buttons.push({ key: `p-${p}`, page: p, label: p, active });
    const pushEllipsis = (id: string) => buttons.push({ key: `e-${id}`, label: '…', disabled: true });
    if (total <= 5) {
        for (let i = 1; i <= total; i++) pushPage(i, i === current);
        return buttons;
    }
    pushPage(1, current === 1);
    if (current > 3) pushEllipsis('l');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pushPage(i, i === current);
    if (current < total - 2) pushEllipsis('r');
    pushPage(total, current === total);
    return buttons;
}

type UserRole = 'manager' | 'worker' | 'admin';
type UserStatus = 'active' | 'inactive';

interface User {
    id: string;
    name: string;
    email: string;
    role: UserRole;
    assignedManager?: string | null;
    status: UserStatus;
    grantSopAccess?: boolean;
}

// API binding helpers
const mapApiUser = (u: ApiAdminUser): User => ({
    id: u.id,
    name: u.name,
    email: u.email,
    role: u.role as UserRole,
    assignedManager: u.assignedManager ?? null,
    status: u.status as UserStatus,
    grantSopAccess: Boolean(u.grantSopAccess),
});


// Component functions
function toTitle(v: string) { return v.replace(/_/g, ' ').replace(/^\w|\s\w/g, (m) => m.toUpperCase()); }

function StatusToggle({ active, onToggle, isLoading }: { active: boolean; onToggle: () => void; isLoading?: boolean }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            disabled={isLoading}
            className={`global-swicth-toggle relative w-12 h-6 rounded-full transition-colors ${active ? 'bg-green-500' : 'bg-red-500'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-pressed={active}
        >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${active ? 'translate-x-5' : 'translate-x-0.5'}`} />
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}
        </button>
    );
}

export const AdminUsers: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [search, setSearch] = useState('');
    const [role, setRole] = useState<'all' | UserRole>('all');
    const [statusTab, setStatusTab] = useState<UserStatus>('active');
    const [page, setPage] = useState(1);
    const [users, setUsers] = useState<User[]>([]);
    const [displayUsers, setDisplayUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [menuFor, setMenuFor] = useState<string | null>(null);
    const [addUserModalOpen, setAddUserModalOpen] = useState(false);
    const [viewUserModalOpen, setViewUserModalOpen] = useState(false);
    const [editUserModalOpen, setEditUserModalOpen] = useState(false);
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
    const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
    const [replaceManagerModalOpen, setReplaceManagerModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);
    const [pageSize, setPageSize] = useState(9);
    const roleDropdownRef = useRef<HTMLDivElement>(null);
    const isUpdatingStatusRef = useRef(false);

    // Load users function
    const loadUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            // Use fetchUsers with pagination parameters
            const res = await apiFetchUsers({
                page,
                pageSize,
                search,
                role: role === 'all' ? undefined : role,
                status: statusTab
            });

            // Filter out current user and admin users
            const filteredItems = res.items.filter(u => {
                if (currentUser && u.email === currentUser.email) return false;
                if (u.role === 'admin') return false;
                return true;
            });

            // Get all users to calculate accurate total and resolve manager names
            const allUsersRes = await apiFetchUsers({
                page: 1,
                pageSize: 1000,
                search,
                role: role === 'all' ? undefined : role,
                status: statusTab
            });

            // Calculate actual total by filtering out admin users and current user from all users
            const allFilteredUsers = allUsersRes.items.filter(u => {
                if (currentUser && u.email === currentUser.email) return false;
                if (u.role === 'admin') return false;
                return true;
            });
            const actualTotal = allFilteredUsers.length;

            // Fetch managers without status filter so inactive workers still resolve active (or inactive) managers
            const allManagersRes = await apiFetchUsers({
                page: 1,
                pageSize: 100,
                role: 'manager',
            });
            const allManagers = allManagersRes.items.filter(u => u.role === 'manager');

            // Create a comprehensive manager lookup map (by ID, email, and name)
            const managerLookupById = new Map<string, string>();
            const managerLookupByEmail = new Map<string, string>();

            allManagers.forEach(manager => {
                managerLookupById.set(manager.id, manager.name);
                managerLookupByEmail.set(manager.email, manager.name);
            });

            // Map users and resolve manager names
            const mappedUsers = filteredItems.map(u => {
                const user = mapApiUser(u);
                // If user has an assignedManager (worker), try to resolve it to a name
                if (user.assignedManager && user.role === 'worker') {
                    let managerName = null;

                    // Try multiple lookup strategies
                    // 1. Direct lookup by ID
                    managerName = managerLookupById.get(user.assignedManager);

                    // 2. If not found by ID, try by email
                    if (!managerName) {
                        managerName = managerLookupByEmail.get(user.assignedManager);
                    }

                    // 3. If still not found, try to find by searching all managers
                    if (!managerName) {
                        const managerBySearch = allManagers.find(m =>
                            m.id === user.assignedManager || m.email === user.assignedManager
                        );
                        if (managerBySearch) {
                            managerName = managerBySearch.name;
                        }
                    }

                    // Update the assignedManager with the resolved name
                    if (managerName) {
                        user.assignedManager = managerName;
                    } else {
                        // If we still can't find the manager, show a fallback
                        logger.warn(`Could not resolve manager for user ${user.name}: ${user.assignedManager}`);
                        user.assignedManager = `Unknown Manager (${user.assignedManager})`;
                    }
                }
                return user;
            });

            setUsers(mappedUsers);
            setDisplayUsers(mappedUsers);
            // Use the actual total of displayable users (after filtering out admin and current user)
            setTotal(actualTotal);
        } catch (e: any) {
            logger.error('Error loading users:', e);
            setUsers([]);
            setDisplayUsers([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, pageSize, search, role, statusTab, currentUser]);

    // Load users by status function (for status-specific filtering)
    const loadUsersByStatus = useCallback(async (status: UserStatus) => {
        setIsLoading(true);
        try {
            // Use fetchUsers with current pagination parameters
            const res = await apiFetchUsers({
                page,
                pageSize,
                search,
                role: role === 'all' ? undefined : role,
                status: status
            });

            // Filter out current user and admin users
            const filteredItems = res.items.filter(u => {
                if (currentUser && u.email === currentUser.email) return false;
                if (u.role === 'admin') return false;
                return true;
            });

            // Get all users to calculate accurate total and resolve manager names
            const allUsersRes = await apiFetchUsers({
                page: 1,
                pageSize: 1000,
                search,
                role: role === 'all' ? undefined : role,
                status: status
            });

            // Calculate actual total by filtering out admin users and current user from all users
            const allFilteredUsers = allUsersRes.items.filter(u => {
                if (currentUser && u.email === currentUser.email) return false;
                if (u.role === 'admin') return false;
                return true;
            });
            const actualTotal = allFilteredUsers.length;

            // Fetch managers without status filter so inactive workers still resolve active (or inactive) managers
            const allManagersRes = await apiFetchUsers({
                page: 1,
                pageSize: 100,
                role: 'manager',
            });
            const allManagers = allManagersRes.items.filter(u => u.role === 'manager');

            // Create a comprehensive manager lookup map (by ID, email, and name)
            const managerLookupById = new Map<string, string>();
            const managerLookupByEmail = new Map<string, string>();

            allManagers.forEach(manager => {
                managerLookupById.set(manager.id, manager.name);
                managerLookupByEmail.set(manager.email, manager.name);
            });

            // Map users and resolve manager names
            const mappedUsers = filteredItems.map(u => {
                const user = mapApiUser(u);
                // If user has an assignedManager (worker), try to resolve it to a name
                if (user.assignedManager && user.role === 'worker') {
                    let managerName = null;

                    // Try multiple lookup strategies
                    // 1. Direct lookup by ID
                    managerName = managerLookupById.get(user.assignedManager);

                    // 2. If not found by ID, try by email
                    if (!managerName) {
                        managerName = managerLookupByEmail.get(user.assignedManager);
                    }

                    // 3. If still not found, try to find by searching all managers
                    if (!managerName) {
                        const managerBySearch = allManagers.find(m =>
                            m.id === user.assignedManager || m.email === user.assignedManager
                        );
                        if (managerBySearch) {
                            managerName = managerBySearch.name;
                        }
                    }

                    // Update the assignedManager with the resolved name
                    if (managerName) {
                        user.assignedManager = managerName;
                    } else {
                        // If we still can't find the manager, show a fallback
                        logger.warn(`Could not resolve manager for user ${user.name}: ${user.assignedManager}`);
                        user.assignedManager = `Unknown Manager (${user.assignedManager})`;
                    }
                }
                return user;
            });

            setUsers(mappedUsers);
            setDisplayUsers(mappedUsers);
            // Use the actual total of displayable users (after filtering out admin and current user)
            setTotal(actualTotal);
        } catch (e: any) {
            logger.error('Error loading users by status:', e);
            setUsers([]);
            setDisplayUsers([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, pageSize, search, role, currentUser]);

    // Removed auto-refresh to prevent unnecessary API calls
    // Users can manually refresh using the refresh button when needed

    // Fetch users from backend whenever filters/page change
    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Reset page when status tab changes
    useEffect(() => {
        setPage(1);
    }, [statusTab]);

    // Calculate total pages and current page
    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
    const currentPage = Math.min(page, totalPages);
    const pageItems = displayUsers; // already server-paginated

    // Filter displayUsers whenever users data changes (for local filtering if needed)
    useEffect(() => {
        // Don't update displayUsers if we're in the middle of a status update
        if (isUpdatingStatusRef.current) {
            return;
        }

        // Since search, role, and status filtering are now handled by the API,
        // we just set displayUsers to match users
        setDisplayUsers(users);
    }, [users]);

    // Ensure page doesn't exceed total pages and handle empty pages
    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
        // If we're on a page with no items and it's not the first page, reset to page 1
        if (pageItems.length === 0 && page > 1 && !isLoading) {
            setPage(1);
        }
    }, [page, totalPages, pageItems.length, isLoading]);

    // Debug logging for pagination issues
    if (total > 0 && pageItems.length === 0 && page > 1) {
        logger.warn(`Pagination issue: page ${page}, totalPages ${totalPages}, total ${total}, pageSize ${pageSize}, items ${pageItems.length}`);
    }

    const handleSearch = (query: string) => {
        setSearch(query);
        setPage(1); // Reset to first page when searching
    };


    const toggleUserStatus = async (id: string) => {
        try {
            setIsUpdatingStatus(id);
            isUpdatingStatusRef.current = true;

            const newStatus = users.find(u => u.id === id)?.status === 'active' ? 'inactive' : 'active';
            const previousRow = users.find(u => u.id === id);
            const res = await apiUpdateUserStatus(id, newStatus);
            const updatedUser = mapApiUser(res.item);

            // Resolve manager name for the updated user if it's a worker
            if (updatedUser.assignedManager && updatedUser.role === 'worker') {
                // Managers-only list, no status filter (matches loadUsers); backend caps page_size at 100
                const managersRes = await apiFetchUsers({ page: 1, pageSize: 100, role: 'manager' });
                const allManagers = managersRes.items.filter(u => u.role === 'manager');

                const managerLookupById = new Map<string, string>();
                const managerLookupByEmail = new Map<string, string>();

                allManagers.forEach(manager => {
                    managerLookupById.set(manager.id, manager.name);
                    managerLookupByEmail.set(manager.email, manager.name);
                });

                let managerName =
                    managerLookupById.get(updatedUser.assignedManager) ||
                    managerLookupByEmail.get(updatedUser.assignedManager);

                if (!managerName) {
                    const managerBySearch = allManagers.find(
                        m => m.id === updatedUser.assignedManager || m.email === updatedUser.assignedManager
                    );
                    if (managerBySearch) {
                        managerName = managerBySearch.name;
                    }
                }

                if (managerName) {
                    updatedUser.assignedManager = managerName;
                } else if (
                    previousRow?.assignedManager &&
                    !previousRow.assignedManager.startsWith('Unknown Manager')
                ) {
                    // Keep displayed name if API returned an id we could not re-resolve (e.g. >100 managers)
                    updatedUser.assignedManager = previousRow.assignedManager;
                } else {
                    console.warn(`Could not resolve manager for updated user ${updatedUser.name}: ${updatedUser.assignedManager}`);
                    updatedUser.assignedManager = `Unknown Manager (${updatedUser.assignedManager})`;
                }
            }

            // Update the user in the main users array
            setUsers(prev => prev.map(u => u.id === id ? updatedUser : u));

            // Manually update displayUsers to bypass the useEffect guard for instant visual feedback
            const filtered = users.map(u => u.id === id ? updatedUser : u).filter((user: User) => {
                const matchesText = search === '' || `${user.name} ${user.email}`.toLowerCase().includes(search.toLowerCase());
                const matchesRole = role === 'all' || user.role === role;
                const matchesStatus = user.status === statusTab;
                return matchesText && matchesRole && matchesStatus;
            });
            setDisplayUsers(filtered);

            toast.success(`User status updated to ${newStatus}`);
        } catch (error: any) {
            const errorMessage = error?.message || "Failed to update user status";
            toast.error(errorMessage);
            logger.error('Error updating user status:', error);
        } finally {
            // Small delay to ensure the ref is properly reset
            setTimeout(() => {
                setIsUpdatingStatus(null);
                isUpdatingStatusRef.current = false;
            }, 100);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
                setIsRoleDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Show preloader while data is loading
    if (initialLoading) {
        return <Preloader />;
    }

    return (
        <div className="space-y-6 min-h-0">
            {/* Controls */}
            <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 items-stretch lg:items-center">
                <div className="w-80">
                    <div className="relative">
                        <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                            <img src="/icons/searchIcon.png" alt="Search" className="w-4 h-4" />
                        </span>
                        <input
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder="Search users.."
                            className="w-full h-10 pl-9 pr-4 py-1 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                        />
                    </div>
                </div>
                <div>
                    <div className="relative" ref={roleDropdownRef}>
                        <button
                            onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                            className="w-48 h-10 px-3 py-2 flex items-center justify-between bg-white border border-gray-300 rounded-xl text-sm text-gray-700 transition-colors shadow-sm hover:bg-gray-50"
                        >
                            <span>{role === 'all' ? 'Select User Type' : role === 'manager' ? 'Manager' : 'Worker'}</span>
                            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isRoleDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-lg z-10">
                                <div className="py-1">
                                    <button
                                        onClick={() => {
                                            setRole('all');
                                            setIsRoleDropdownOpen(false);
                                            setPage(1);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                    >
                                        Select User Type
                                    </button>
                                    <button
                                        onClick={() => {
                                            setRole('manager');
                                            setIsRoleDropdownOpen(false);
                                            setPage(1);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                    >
                                        Manager
                                    </button>
                                    <button
                                        onClick={() => {
                                            setRole('worker');
                                            setIsRoleDropdownOpen(false);
                                            setPage(1);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                    >
                                        Worker
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center">
                    <div className="flex items-center rounded-xl border border-gray-300 overflow-hidden bg-white p-1 w-48 h-10">
                        <button
                            className={`flex-1 h-8 text-sm rounded-md transition-all ${statusTab === 'active' ? 'bg-green-600 text-white shadow-sm font-semibold' : 'text-gray-500'}`}
                            onClick={() => {
                                setStatusTab('active');
                                setPage(1);
                            }}
                        >
                            Active
                        </button>
                        <button
                            className={`flex-1 h-8 text-sm rounded-md transition-all ${statusTab === 'inactive' ? 'bg-red-500 text-white shadow-sm font-semibold' : 'text-gray-500'}`}
                            onClick={() => {
                                setStatusTab('inactive');
                                setPage(1);
                            }}
                        >
                            Inactive
                        </button>
                    </div>
                </div>
                <div className="flex-none ml-auto flex gap-3">
                    <button
                        onClick={() => {
                            loadUsers();
                        }}
                        className="renegade-btn-secondary mini-btn"
                        title="Refresh users list"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Refresh
                    </button>
                    <button
                        onClick={() => setAddUserModalOpen(true)}
                        className="renegade-btn-primary mini-btn"
                    >
                        <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M0.5 13C0.5 6.09625 6.09625 0.5 13 0.5C19.9037 0.5 25.5 6.09625 25.5 13C25.5 19.9037 19.9037 25.5 13 25.5C6.09625 25.5 0.5 19.9037 0.5 13ZM13 3C10.3478 3 7.8043 4.05357 5.92893 5.92893C4.05357 7.8043 3 10.3478 3 13C3 15.6522 4.05357 18.1957 5.92893 20.0711C7.8043 21.9464 10.3478 23 13 23C15.6522 23 18.1957 21.9464 20.0711 20.0711C21.9464 18.1957 23 15.6522 23 13C23 10.3478 21.9464 7.8043 20.0711 5.92893C18.1957 4.05357 15.6522 3 13 3Z" fill="white" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M14.25 6.75C14.25 6.41848 14.1183 6.10054 13.8839 5.86612C13.6495 5.6317 13.3315 5.5 13 5.5C12.6685 5.5 12.3505 5.6317 12.1161 5.86612C11.8817 6.10054 11.75 6.41848 11.75 6.75V11.75H6.75C6.41848 11.75 6.10054 11.8817 5.86612 12.1161C5.6317 12.3505 5.5 12.6685 5.5 13C5.5 13.3315 5.6317 13.6495 5.86612 13.8839C6.10054 14.1183 6.41848 14.25 6.75 14.25H11.75V19.25C11.75 19.5815 11.8817 19.8995 12.1161 20.1339C12.3505 20.3683 12.6685 20.5 13 20.5C13.3315 20.5 13.6495 20.3683 13.8839 20.1339C14.1183 19.8995 14.25 19.5815 14.25 19.25V14.25H19.25C19.5815 14.25 19.8995 14.1183 20.1339 13.8839C20.3683 13.6495 20.5 13.3315 20.5 13C20.5 12.6685 20.3683 12.3505 20.1339 12.1161C19.8995 11.8817 19.5815 11.75 19.25 11.75H14.25V6.75Z" fill="white" />
                        </svg>
                        Add New User
                    </button>
                </div>
            </div>

            {/* Users table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Name</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Email</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Role</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Assigned Manager</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Status</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-4 text-14-bold grey-800">Loading...</td>
                                </tr>
                            ) : pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">No users found</td>
                                </tr>
                            ) : (
                                pageItems.map((u) => (
                                    <tr key={u.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-14-bold grey-800">{u.name}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{u.email}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{toTitle(u.role)}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">
                                            {u.role === 'manager' ? '—' : (u.assignedManager || '—')}
                                        </td>
                                        <td className="px-4 py-4">
                                            <StatusToggle
                                                active={u.status === 'active'}
                                                onToggle={() => toggleUserStatus(u.id)}
                                                isLoading={isUpdatingStatus === u.id}
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <ActionsMenu
                                                user={u}
                                                isOpen={menuFor === u.id}
                                                onToggle={() => setMenuFor(v => v === u.id ? null : u.id)}
                                                onClose={() => setMenuFor(null)}
                                                onViewUser={(user) => {
                                                    setSelectedUser(user);
                                                    setViewUserModalOpen(true);
                                                }}
                                                onEditUser={(user) => {
                                                    setSelectedUser(user);
                                                    setEditUserModalOpen(true);
                                                }}
                                                onResetPassword={(user) => {
                                                    setSelectedUser(user);
                                                    setResetPasswordModalOpen(true);
                                                }}
                                                onDeleteUser={(user) => {
                                                    setSelectedUser(user);
                                                    setDeleteUserModalOpen(true);
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="global-pagination px-4 md:px-6 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            className="btn-next-prev"
                            disabled={currentPage === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            <i className="iconoir-nav-arrow-left"></i>
                        </button>
                        <div className="flex items-center gap-1">
                            {getPageNumbers(currentPage, totalPages).map((p: PageButton) => (
                                <button
                                    key={p.key}
                                    disabled={p.disabled}
                                    onClick={() => typeof p.page === 'number' && setPage(p.page)}
                                    className={`btn-page-number ${p.active ? 'color-renegade' : ''}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button
                            className="btn-next-prev"
                            disabled={currentPage >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            <i className="iconoir-nav-arrow-right"></i>
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={pageSize}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value) || 10;
                                if (newPageSize >= 1 && newPageSize <= 100) {
                                    setPageSize(newPageSize);
                                    setPage(1); // Reset to first page when page size changes
                                }
                            }}
                            className="page-size-input"
                        />
                        <span className="text-14-bold grey-400">Rows per page</span>
                    </div>
                </div>
            </div>

            {/* Add New User Modal */}
            {addUserModalOpen && (
                <AddNewUserModal
                    onClose={() => setAddUserModalOpen(false)}
                    onCreated={(newUser) => {
                        // Refresh the users list and reset to first page
                        loadUsers();
                        setPage(1); // Reset to first page to show the new user
                    }}
                />
            )}

            {/* User Info Modal */}
            {viewUserModalOpen && selectedUser && (
                <UserInfoModal
                    user={selectedUser}
                    onClose={() => {
                        setViewUserModalOpen(false);
                        setSelectedUser(null);
                    }}
                    onEditUser={(user) => {
                        setSelectedUser(user);
                        setViewUserModalOpen(false);
                        setEditUserModalOpen(true);
                    }}
                />
            )}

            {/* Edit User Modal */}
            {editUserModalOpen && selectedUser && (
                <EditUserModal
                    user={selectedUser}
                    onUpdated={(u) => {
                        // Refresh the users list to get updated data
                        loadUsers();
                    }}
                    onClose={() => {
                        setEditUserModalOpen(false);
                        setSelectedUser(null);
                    }}
                />
            )}

            {/* Reset Password Modal */}
            {resetPasswordModalOpen && selectedUser && (
                <ResetPasswordModal
                    user={selectedUser}
                    onClose={() => {
                        setResetPasswordModalOpen(false);
                        setSelectedUser(null);
                    }}
                />
            )}

            {/* Delete User Modal */}
            {deleteUserModalOpen && selectedUser && (
                <DeleteUserModal
                    user={selectedUser}
                    onDeleted={(id) => {
                        // Refresh the users list to reflect the deletion
                        loadUsers();
                    }}
                    onClose={() => {
                        setDeleteUserModalOpen(false);
                        setSelectedUser(null);
                    }}
                    onShowReplaceManagerModal={(user) => {
                        setSelectedUser(user);
                        setReplaceManagerModalOpen(true);
                    }}
                />
            )}

            {/* Replace Manager Modal */}
            {replaceManagerModalOpen && selectedUser && selectedUser.role === 'manager' && (
                <ReplaceManagerModal
                    managerToDelete={selectedUser}
                    onSuccess={(message) => {
                        toast.success(message);
                        // Refresh the users list to reflect changes
                        loadUsers();
                        setPage(1); // Reset to first page to reflect changes
                    }}
                    onClose={() => {
                        setReplaceManagerModalOpen(false);
                        setSelectedUser(null);
                    }}
                />
            )}
        </div>
    );
};

function ActionsMenu({ user, isOpen, onToggle, onClose, onViewUser, onEditUser, onResetPassword, onDeleteUser }: { user: User; isOpen: boolean; onToggle: () => void; onClose: () => void; onViewUser: (user: User) => void; onEditUser: (user: User) => void; onResetPassword: (user: User) => void; onDeleteUser: (user: User) => void }) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

    function computePosition() {
        const btn = buttonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();

        // Use measured size if available, otherwise sensible defaults
        const menuWidth = (menuRef.current?.offsetWidth) ?? 224;
        const menuHeight = (menuRef.current?.offsetHeight) ?? 200;

        const margin = 8;
        // Prefer bottom-right aligned to trigger right edge
        let top = rect.bottom + margin;
        let left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - margin);

        // Flip up if not enough room below
        if (top + menuHeight > window.innerHeight) {
            top = rect.top - menuHeight - margin;
        }
        // Clamp within viewport
        if (top < margin) top = margin;
        if (left < margin) left = margin;

        setCoords({ top, left });
    }

    useLayoutEffect(() => {
        if (!isOpen) return;
        computePosition();
        const handle = () => computePosition();
        window.addEventListener('scroll', handle, true);
        window.addEventListener('resize', handle);
        return () => {
            window.removeEventListener('scroll', handle, true);
            window.removeEventListener('resize', handle);
        };
    }, [isOpen]);

    useEffect(() => {
        function onDocClick(e: MouseEvent) {
            const target = e.target as Node;
            if (
                menuRef.current && !menuRef.current.contains(target) &&
                buttonRef.current && !buttonRef.current.contains(target)
            ) onClose();
        }
        function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
        if (isOpen) {
            document.addEventListener('mousedown', onDocClick);
            document.addEventListener('keydown', onKey);
        }
        return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
    }, [isOpen, onClose]);

    // Render the menu with visibility hidden first to allow measurement, then show
    const shouldRender = isOpen;
    const isVisible = !!coords;
    const menu = shouldRender ? (
        <div
            ref={menuRef}
            style={{ position: 'fixed', top: (coords?.top ?? -9999), left: (coords?.left ?? -9999), zIndex: 1000, visibility: isVisible ? 'visible' : 'hidden' }}
            className="w-56 rounded-2xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-200"
        >
            <div className="py-2">
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onViewUser(user);
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Eye size={16} />
                    </span>
                    <span className="text-sm text-gray-800">View</span>
                </button>
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onEditUser(user);
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                        <Pencil size={16} />
                    </span>
                    <span className="text-sm text-gray-800">Edit</span>
                </button>
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onResetPassword(user);
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-yellow-50 text-yellow-600 flex items-center justify-center">
                        <Lock size={16} />
                    </span>
                    <span className="text-sm text-gray-800">Reset Password</span>
                </button>
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onDeleteUser(user);
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                        <Trash2 size={16} />
                    </span>
                    <span className="text-sm text-gray-800">Delete</span>
                </button>
            </div>
        </div>
    ) : null;

    return (
        <div ref={containerRef} className="relative inline-block text-left">
            <button ref={buttonRef} type="button" onClick={() => { onToggle(); setTimeout(computePosition, 0); }} className="px-2 py-1 rounded-lg hover:bg-gray-100">
                <svg width="26" height="6" viewBox="0 0 26 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6C2.20435 6 1.44129 5.68393 0.87868 5.12132C0.316071 4.55871 0 3.79565 0 3C0 2.20435 0.316071 1.44129 0.87868 0.878679C1.44129 0.31607 2.20435 0 3 0C3.79565 0 4.55871 0.31607 5.12132 0.878679C5.68393 1.44129 6 2.20435 6 3C6 3.79565 5.68393 4.55871 5.12132 5.12132C4.55871 5.68393 3.79565 6 3 6ZM13 6C12.2044 6 11.4413 5.68393 10.8787 5.12132C10.3161 4.55871 10 3.79565 10 3C10 2.20435 10.3161 1.44129 10.8787 0.878679C11.4413 0.31607 12.2044 0 13 0C13.7956 0 14.5587 0.31607 15.1213 0.878679C15.6839 1.44129 16 2.20435 16 3C16 3.79565 15.6839 4.55871 15.1213 5.12132C14.5587 5.68393 13.7956 6 13 6ZM23 6C22.2044 6 21.4413 5.68393 20.8787 5.12132C20.3161 4.55871 20 3.79565 20 3C20 2.20435 20.3161 1.44129 20.8787 0.878679C21.4413 0.31607 22.2044 0 23 0C23.7956 0 24.5587 0.31607 25.1213 0.878679C25.6839 1.44129 26 2.20435 26 3C26 3.79565 25.6839 4.55871 25.1213 5.12132C24.5587 5.68393 23.7956 6 23 6Z" fill="#616161" />
                </svg>
            </button>
            {menu && ReactDOM.createPortal(menu, document.body)}
        </div>
    );
}

// Add New User Modal Component
function AddNewUserModal({ onClose, onCreated }: { onClose: () => void; onCreated?: (u: User) => void }) {
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userRole, setUserRole] = useState<"manager" | "worker">("worker");
    const [selectedManager, setSelectedManager] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [managers, setManagers] = useState<ApiAdminUser[]>([]);
    const [managersLoading, setManagersLoading] = useState(false);
    const [emailError, setEmailError] = useState<string>("");
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [isManagerDropdownOpen, setIsManagerDropdownOpen] = useState(false);
    const managerDropdownRef = useRef<HTMLDivElement>(null);
    const [grantSopAccess, setGrantSopAccess] = useState(false);

    // Validation errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Fetch managers when modal opens
    useEffect(() => {
        const loadManagers = async () => {
            try {
                setManagersLoading(true);
                const res = await apiFetchUsers({ page: 1, pageSize: 100, role: 'manager', status: 'active' });
                setManagers(res.items);
            } catch (error) {
                logger.error('Error fetching managers:', error);
            } finally {
                setManagersLoading(false);
            }
        };
        loadManagers();
    }, []);

    // Close manager dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (managerDropdownRef.current && !managerDropdownRef.current.contains(event.target as Node)) {
                setIsManagerDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleManagerChange = (managerEmail: string) => {
        setSelectedManager(managerEmail);
        validateField('manager', managerEmail);
    };

    // Debounced email check
    useEffect(() => {
        if (!userEmail.trim()) {
            setEmailError("");
            return;
        }

        const timeoutId = setTimeout(() => {
            checkEmailExists(userEmail);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [userEmail]);

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!userName.trim()) {
            newErrors.name = 'User Name is required';
        }

        if (!userEmail.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(userEmail)) {
            newErrors.email = 'Email is invalid';
        }

        if (userRole === "worker" && !selectedManager) {
            newErrors.manager = 'Manager assignment is required for Field Workers';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'name') {
            if (!value.trim()) {
                newErrors.name = 'User Name is required';
            } else {
                delete newErrors.name;
            }
        } else if (fieldName === 'email') {
            if (!value.trim()) {
                newErrors.email = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(value)) {
                newErrors.email = 'Email is invalid';
            } else {
                delete newErrors.email;
            }
        } else if (fieldName === 'manager') {
            if (userRole === "worker" && !value) {
                newErrors.manager = 'Manager assignment is required for Field Workers';
            } else {
                delete newErrors.manager;
            }
        }

        setErrors(newErrors);
    };

    // Check if email exists
    const checkEmailExists = async (email: string) => {
        if (!email.trim()) {
            setEmailError("");
            return false;
        }

        try {
            setCheckingEmail(true);
            setEmailError("");
            const res = await apiFetchUsers({ page: 1, pageSize: 100, search: email.trim() });
            const existingUser = res.items.find(user => user.email.toLowerCase() === email.trim().toLowerCase());

            if (existingUser) {
                setEmailError("Email already exists");
                return true;
            } else {
                setEmailError("");
                return false;
            }
        } catch (error) {
            logger.error('Error checking email:', error);
            setEmailError("Error checking email");
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Check if email already exists
        const emailExists = await checkEmailExists(userEmail.trim());
        if (emailExists) {
            return; // Error message is already set by checkEmailExists
        }

        setIsSaving(true);
        setErrors({});

        try {
            const res = await apiCreateUser({
                name: userName.trim(),
                email: userEmail.trim(),
                role: userRole,
                assignedManager: userRole === "worker" ? (selectedManager || undefined) : undefined,
                status: 'active',
                grant_sop_access: grantSopAccess,
            });
            const newUser = mapApiUser(res.item as unknown as ApiAdminUser);

            toast.success("User created successfully");
            onCreated?.(newUser);
            onClose();
        } catch (error: any) {
            const errorMessage = error?.message || "Failed to create user. Please try again.";
            setErrors({ general: errorMessage });
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={true} onHide={onClose} className='std-modal-width' centered backdrop="static">
            <Modal.Header>
                <Modal.Title>
                    Add New User
                </Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <form id="add-user-form" onSubmit={handleSubmit}>
                    {/* User Name */}
                    <div className="form-group">
                        <label htmlFor="userName">User Name</label>
                        <input
                            id="userName"
                            type="text"
                            value={userName}
                            onChange={(e) => {
                                setUserName(e.target.value);
                                validateField('name', e.target.value);
                            }}
                            className={`form-control ${errors.name ? 'border-danger' : ''}`}
                            placeholder="Enter user name"
                        />
                        {errors.name && (
                            <p className="error-message">{errors.name}</p>
                        )}
                    </div>

                    {/* User Email ID */}
                    <div className="form-group">
                        <label htmlFor="userEmail">User Email ID</label>
                        <div className="position-relative">
                            <input
                                id="userEmail"
                                type="email"
                                value={userEmail}
                                onChange={(e) => {
                                    setUserEmail(e.target.value);
                                    validateField('email', e.target.value);
                                }}
                                className={`form-control ${errors.email || emailError ? 'border-danger' : ''}`}
                                placeholder="Enter user email"
                            />
                            {checkingEmail && (
                                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                    <Spinner size="sm" />
                                </div>
                            )}
                        </div>
                        {(errors.email || emailError) && (
                            <p className="error-message">{errors.email || emailError}</p>
                        )}
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            {/* User Role Selection */}
                            <div className="form-group">
                                <label>User Role</label>
                                <div className="flex bg-white border border-secondary rounded-md p-1" style={{ height: '54px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setUserRole("manager")}
                                        className={`flex-1 px-4 py-2 text-14-bold rounded-md transition-all ${userRole === "manager"
                                            ? "bg-black text-white shadow-sm"
                                            : "text-gray-600 hover:text-gray-800"
                                            }`}
                                    >
                                        Manager
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setUserRole("worker")}
                                        className={`flex-1 px-4 py-2 text-14-bold rounded-md transition-all ${userRole === "worker"
                                            ? "bg-black text-white shadow-sm"
                                            : "text-gray-600 hover:text-gray-800"
                                            }`}
                                    >
                                        Field Worker
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            {/* Select Manager - Only show for Field Worker */}
                            {userRole === "worker" && (
                                <div className="form-group">
                                    <label htmlFor="selectedManager">Assign Manager </label>
                                    <div className="relative" ref={managerDropdownRef}>
                                        <button
                                            title={selectedManager}
                                            type="button"
                                            onClick={() => setIsManagerDropdownOpen(!isManagerDropdownOpen)}
                                            className={`custom-dropdown-btn ${errors.manager ? 'border-danger' : ''}`}
                                        >
                                            <span className={`dp-manager-name ${selectedManager === '' ? 'grey-500' : 'grey-800'}`} >
                                                {selectedManager === ''
                                                    ? 'Select Assign Manager'
                                                    : managers.find(m => m.email === selectedManager)
                                                        ? `${managers.find(m => m.email === selectedManager)?.name} (${selectedManager})`
                                                        : 'Select Assign Manager'
                                                }
                                            </span>
                                            <ChevronDown
                                                className={`w-4 h-4 grey-500 transition-transform ${isManagerDropdownOpen ? 'rotate-180' : ''}`}
                                            />
                                        </button>
                                        {isManagerDropdownOpen && (
                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-lg" style={{ zIndex: 10, maxHeight: '250px', overflowY: 'auto' }}>
                                                <div className="py-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleManagerChange('');
                                                            setIsManagerDropdownOpen(false);
                                                        }}
                                                        className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                    >
                                                        Select Assign Manager
                                                    </button>
                                                    {managersLoading && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            <div className="px-3 py-2 text-start text-14-reg grey-500">
                                                                Loading managers...
                                                            </div>
                                                        </>
                                                    )}
                                                    {!managersLoading && managers.length === 0 && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            <div className="px-3 py-2 text-start text-14-reg grey-500">
                                                                No managers available
                                                            </div>
                                                        </>
                                                    )}
                                                    {!managersLoading && managers.length > 0 && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            {managers.map((manager, index) => (
                                                                <div key={manager.id}>
                                                                    <button
                                                                        title={manager.email}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            handleManagerChange(manager.email);
                                                                            setIsManagerDropdownOpen(false);
                                                                        }}
                                                                        className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light dp-manager-name"
                                                                    >
                                                                        {manager.name} ({manager.email})
                                                                    </button>
                                                                    {index < managers.length - 1 && (
                                                                        <div className="border-top border-dashed"></div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.manager && (
                                        <p className="error-message">{errors.manager}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                checked={grantSopAccess}
                                onChange={(e) => setGrantSopAccess(e.target.checked)}
                            />
                            <span>
                                Grant access to all existing SOPs (based on role)
                            </span>
                        </label>

                        <p className="form-helper-text">
                            This will automatically give the user access to all SOPs they are eligible for based on their role.
                        </p>
                    </div>

                    {/* General Error Message */}
                    {errors.general && (
                        <div className="alert alert-danger" role="alert">
                            {errors.general}
                        </div>
                    )}
                </form>
                <div className="d-flex justify-content-end gap-3 mt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="renegade-btn-secondary mini-btn"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="add-user-form"
                        disabled={isSaving}
                        className="renegade-btn-primary mini-btn"
                        onClick={handleSubmit}
                    >
                        {isSaving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Adding User...
                            </>
                        ) : (
                            'Add New User'
                        )}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

// Edit User Modal Component
function EditUserModal({ user, onClose, onUpdated }: { user: User; onClose: () => void; onUpdated?: (u: User) => void }) {
    const [userName, setUserName] = useState(user.name);
    const [userEmail] = useState(user.email);
    const [userRole, setUserRole] = useState<"manager" | "worker">(user.role === 'admin' ? 'manager' : user.role as "manager" | "worker");
    const [selectedManager, setSelectedManager] = useState(user.assignedManager || "");
    const [grantSopAccess, setGrantSopAccess] = useState(Boolean(user.grantSopAccess));

    const [isSaving, setIsSaving] = useState(false);
    const [managers, setManagers] = useState<ApiAdminUser[]>([]);
    const [managersLoading, setManagersLoading] = useState(false);
    const [isManagerDropdownOpen, setIsManagerDropdownOpen] = useState(false);
    const managerDropdownRef = useRef<HTMLDivElement>(null);

    // Validation errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Fetch managers when modal opens and resolve manager name to ID
    useEffect(() => {
        const loadManagers = async () => {
            try {
                setManagersLoading(true);
                const res = await apiFetchUsers({ page: 1, pageSize: 100, role: 'manager', status: 'active' });
                setManagers(res.items);

                // If user has an assigned manager, find the manager email by name
                if (user.assignedManager && user.role === 'worker') {
                    const managerData = res.items.find(m => m.name === user.assignedManager);
                    if (managerData) {
                        setSelectedManager(managerData.email);
                    }
                }
            } catch (error) {
                logger.error('Error fetching managers:', error);
            } finally {
                setManagersLoading(false);
            }
        };
        loadManagers();
    }, [user.assignedManager, user.role]);

    const handleManagerChange = (managerEmail: string) => {
        setSelectedManager(managerEmail);
        validateEditField('manager', managerEmail);
    };

    // Close manager dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (managerDropdownRef.current && !managerDropdownRef.current.contains(event.target as Node)) {
                setIsManagerDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const validateEditField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'name') {
            if (!value.trim()) {
                newErrors.name = 'User Name is required';
            } else {
                delete newErrors.name;
            }
        } else if (fieldName === 'manager') {
            if (userRole === "worker" && !value) {
                newErrors.manager = 'Manager assignment is required for Field Workers';
            } else {
                delete newErrors.manager;
            }
        }

        setErrors(newErrors);
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!userName.trim()) {
            newErrors.name = 'User Name is required';
        }

        if (userRole === "worker" && !selectedManager) {
            newErrors.manager = 'Manager assignment is required for Field Workers';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /** Managers cannot be demoted to field workers from this screen; role stays manager. */
    const isManagerRoleLocked = user.role === 'manager';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSaving(true);
        setErrors({});

        try {
            const effectiveRole: 'manager' | 'worker' = isManagerRoleLocked ? 'manager' : userRole;
            const res = await apiUpdateUser(user.id, {
                name: userName.trim(),
                role: effectiveRole,
                assignedManager: effectiveRole === "worker" ? (selectedManager || null) : null,
                grant_sop_access: grantSopAccess,
            });
            const updated = mapApiUser(res.item as unknown as ApiAdminUser);

            toast.success("User updated successfully!");
            onUpdated?.(updated);
            onClose();
        } catch (error: any) {
            const errorMessage = error?.message || "Failed to update user. Please try again.";
            setErrors({ general: errorMessage });
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={true} onHide={onClose} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Edit User Details</Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                {/* Form */}
                <div>
                    {/* User Name */}
                    <div className="form-group">
                        <label htmlFor="userName">User Name</label>
                        <input
                            id="editUserName"
                            type="text"
                            value={userName}
                            onChange={(e) => {
                                setUserName(e.target.value);
                                validateEditField('name', e.target.value);
                            }}
                            placeholder="John Patrics"
                            className={`form-control ${errors.name ? 'border-danger' : ''}`}
                        />
                        {errors.name && (
                            <p className="error-message">{errors.name}</p>
                        )}
                    </div>

                    {/* User Email ID (non-editable) */}
                    <div className="form-group">
                        <label htmlFor="editUserEmail">User Email ID</label>
                        <input
                            id="editUserEmail"
                            type="email"
                            value={userEmail}
                            readOnly
                            disabled
                            className="form-control"
                            style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                        />
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            {/* User Role Selection */}
                            <div className="form-group">
                                <label htmlFor="userRole">User Role</label>
                                <div
                                    className="flex bg-white border border-secondary rounded-md p-1"
                                    style={{ height: '54px' }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setUserRole("manager")}
                                        className={`flex-1 px-4 py-2 text-14-bold rounded-md transition-all ${userRole === "manager"
                                            ? "bg-black text-white shadow-sm"
                                            : "text-gray-600 hover:text-gray-800"
                                            }`}
                                    >
                                        Manager
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isManagerRoleLocked}
                                        onClick={() => setUserRole("worker")}
                                        className={`flex-1 px-4 py-2 text-14-bold rounded-md transition-all disabled:cursor-not-allowed ${userRole === "worker" && !isManagerRoleLocked
                                            ? "bg-black text-white shadow-sm"
                                            : isManagerRoleLocked
                                                ? "text-gray-400"
                                                : "text-gray-600 hover:text-gray-800"
                                            }`}
                                    >
                                        Field Worker
                                    </button>
                                </div>

                            </div>
                        </div>
                        <div className="col-md-6">
                            {/* Select Manager - Only show for Field Worker */}
                            {userRole === "worker" && (
                                <div className="form-group">
                                    <label htmlFor="selectedManager">Assign Manager</label>
                                    <div className="relative" ref={managerDropdownRef}>
                                        <button
                                            title={selectedManager}
                                            type="button"
                                            onClick={() => setIsManagerDropdownOpen(!isManagerDropdownOpen)}
                                            className={`custom-dropdown-btn`}
                                        >
                                            <span className={`dp-manager-name ${selectedManager === '' ? 'grey-500' : 'grey-800'}`} >
                                                {selectedManager === ''
                                                    ? 'Select Assign Manager'
                                                    : managers.find(m => m.email === selectedManager)
                                                        ? `${managers.find(m => m.email === selectedManager)?.name} (${selectedManager})`
                                                        : 'Select Assign Manager'
                                                }
                                            </span>
                                            <ChevronDown
                                                className={`w-4 h-4 grey-500 transition-transform ${isManagerDropdownOpen ? 'rotate-180' : ''}`}
                                            />
                                        </button>
                                        {isManagerDropdownOpen && (
                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-lg" style={{ zIndex: 10, maxHeight: '250px', overflowY: 'auto' }}>
                                                <div className="py-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleManagerChange('');
                                                            setIsManagerDropdownOpen(false);
                                                        }}
                                                        className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                    >
                                                        Select Assign Manager
                                                    </button>
                                                    {managersLoading && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            <div className="px-3 py-2 text-start text-14-reg grey-500">
                                                                Loading managers...
                                                            </div>
                                                        </>
                                                    )}
                                                    {!managersLoading && managers.length === 0 && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            <div className="px-3 py-2 text-start text-14-reg grey-500">
                                                                No managers available
                                                            </div>
                                                        </>
                                                    )}
                                                    {!managersLoading && managers.length > 0 && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            {managers
                                                                .filter((manager) => manager.id !== user.id)
                                                                .map((manager, index, allFilteredManagers) => (
                                                                    <div key={manager.id}>
                                                                        <button
                                                                            title={manager.email}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                handleManagerChange(manager.email);
                                                                                setIsManagerDropdownOpen(false);
                                                                            }}
                                                                            className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light dp-manager-name"
                                                                        >
                                                                            {manager.name} ({manager.email})
                                                                        </button>
                                                                        {index < allFilteredManagers.length - 1 && (
                                                                            <div className="border-top border-dashed"></div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                checked={grantSopAccess}
                                onChange={(e) => setGrantSopAccess(e.target.checked)}
                            />
                            <span>
                                Grant access to all existing SOPs (based on role)
                            </span>
                        </label>
                        <p className="form-helper-text">
                            This will automatically give the user access to all SOPs they are eligible for based on their role.
                        </p>
                    </div>
                </div>
                <div className="d-flex justify-content-end gap-3 mt-4">
                    <button
                        onClick={onClose}
                        className="renegade-btn-secondary mini-btn"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="renegade-btn-primary mini-btn"
                    >
                        {isSaving ? 'Saving...' : 'Update'}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

// Reset Password Modal Component
function ResetPasswordModal({ user, onClose }: { user: User; onClose: () => void }) {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSaving, setIsSaving] = useState(false);

    const validatePasswordForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!newPassword) {
            newErrors.newPassword = 'New password is required.';
        } else if (newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long.';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(newPassword)) {
            newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required.';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'newPassword') {
            if (!value) {
                newErrors.newPassword = 'New password is required.';
            } else if (value.length < 8) {
                newErrors.newPassword = 'Password must be at least 8 characters long.';
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(value)) {
                newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
            } else {
                delete newErrors.newPassword;
            }

            // Also validate confirm password when new password changes
            if (confirmPassword && confirmPassword !== value) {
                newErrors.confirmPassword = 'Passwords do not match';
            } else if (confirmPassword && confirmPassword === value) {
                delete newErrors.confirmPassword;
            }
        } else if (fieldName === 'confirmPassword') {
            if (!value) {
                newErrors.confirmPassword = 'Confirm password is required.';
            } else if (newPassword !== value) {
                newErrors.confirmPassword = 'Passwords do not match';
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});

        // Validation using the validation function
        if (!validatePasswordForm()) {
            return;
        }

        setIsSaving(true);

        try {
            await apiUpdateUserPassword(user.id, newPassword, confirmPassword);
            toast.success("Password updated successfully!");
            // Clear form fields on success
            setNewPassword("");
            setConfirmPassword("");
            onClose();
        } catch (error: any) {
            // Extract error message from response
            let errorMessage = "Failed to update password. Please try again.";

            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            // Do not use error.response?.data?.detail - could expose sensitive backend data

            // Show specific error messages for different failure scenarios
            if (errorMessage.includes("verification failed") ||
                errorMessage.includes("write may not have been committed") ||
                errorMessage.includes("verification failed after update")) {
                errorMessage = "Password update may not have been saved. Please try again or contact support if the issue persists.";
            } else if (errorMessage.includes("hashed_password field is missing")) {
                errorMessage = "Password update failed due to a data issue. Please contact support.";
            } else if (errorMessage.includes("user not found after update")) {
                errorMessage = "Password update failed - user account issue detected. Please try again or contact support.";
            } else if (errorMessage.includes("Passwords do not match")) {
                errorMessage = "The passwords you entered do not match. Please try again.";
            } else if (errorMessage.includes("at least 8 characters")) {
                errorMessage = "Password must be at least 8 characters long.";
            }

            toast.error(errorMessage);
            logger.error('Password update error:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={true} onHide={onClose} centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Reset Password</Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <form onSubmit={handleSubmit}>
                    {/* Enter New Password */}
                    <div className="form-group password-feild">
                        <label htmlFor="newPassword">Enter New Password</label>
                        <input
                            id="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                validatePasswordField('newPassword', e.target.value);
                            }}
                            placeholder="Enter new password"
                            autoComplete="new-password"
                            className={`form-control ${errors.newPassword ? 'border-danger' : ''}`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className='password-toggle'
                        >
                            {showNewPassword ? (
                                <i className="iconoir-eye"></i>
                            ) : (
                                <i className="iconoir-eye-closed"></i>
                            )}
                        </button>
                        {errors.newPassword && (
                            <p className="error-message">{errors.newPassword}</p>
                        )}
                    </div>

                    {/* Enter Confirm Password */}
                    <div className="form-group password-feild">
                        <label htmlFor="confirmPassword">Enter Confirm Password</label>
                        <input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                validatePasswordField('confirmPassword', e.target.value);
                            }}
                            placeholder="Enter confirm password"
                            autoComplete="new-password"
                            className={`form-control ${errors.confirmPassword ? 'border-danger' : ''}`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className='password-toggle'
                        >
                            {showConfirmPassword ? (
                                <i className="iconoir-eye"></i>
                            ) : (
                                <i className="iconoir-eye-closed"></i>
                            )}
                        </button>
                        {errors.confirmPassword && (
                            <p className="error-message">{errors.confirmPassword}</p>
                        )}
                    </div>
                    <div className="d-flex justify-content-end gap-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="renegade-btn-secondary mini-btn"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="renegade-btn-primary mini-btn"
                        >
                            {isSaving ? 'Updating...' : 'Update Password'}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
}

// Delete User Modal Component
function DeleteUserModal({ user, onClose, onDeleted, onShowReplaceManagerModal }: { user: User; onClose: () => void; onDeleted?: (id: string) => void; onShowReplaceManagerModal?: (user: User) => void }) {
    const [isDeleting, setIsDeleting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    const validateManagerDeletion = async () => {
        if (user.role !== 'manager') {
            return { canDelete: true, reason: null };
        }

        try {
            // Get all users to check manager-worker relationships
            const res = await apiFetchUsers({ page: 1, pageSize: 1000 });
            const allUsers = res.items.filter(u => u.email !== user.email);


            // Check if any workers are assigned to this manager
            // Try multiple ways to match (ID, email, etc.)
            const assignedWorkers = allUsers.filter(u => {
                if (u.role !== 'worker') return false;

                // Try different matching strategies
                const idMatch = u.assignedManager === user.id;
                const emailMatch = u.assignedManager === user.email;

                const matches = idMatch || emailMatch;


                return matches;
            });

            // Check if there are other active managers
            const otherManagers = allUsers.filter(u =>
                u.role === 'manager' && u.status === 'active'
            );


            if (assignedWorkers.length === 0) {
                // No workers assigned - safe to delete
                return { canDelete: true, reason: null };
            }

            if (otherManagers.length === 0) {
                // Workers assigned but no other managers - block deletion
                return {
                    canDelete: false,
                    reason: "Cannot delete. Please add another manager first to reassign users."
                };
            }

            // Workers assigned and other managers exist - require replacement
            return {
                canDelete: false,
                reason: "This manager has assigned workers. Please select a replacement manager to reassign them",
                showReplacementModal: true
            };

        } catch (error) {
            logger.error('Error validating manager deletion:', error);
            // If validation fails, show replacement modal by default for managers
            // This is a safety fallback to prevent accidental manager deletion
            return {
                canDelete: false,
                reason: "Unable to validate manager assignments. Please select a replacement manager to proceed.",
                showReplacementModal: true
            };
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        setIsValidating(true);

        try {
            // First, validate manager deletion on frontend for additional safety
            const validation = await validateManagerDeletion();

            if (!validation.canDelete) {
                setIsValidating(false);
                if (validation.showReplacementModal) {
                    onClose(); // Close this modal
                    onShowReplaceManagerModal?.(user); // Open replacement modal
                    return;
                } else {
                    toast.error(validation.reason || "Cannot delete this manager.");
                    onClose();
                    return;
                }
            }

            // Call API to perform deletion
            const result = await deleteUser(user.id);

            // Check if this was a direct delete (manager with no workers) or successful worker deletion
            if (result.direct_delete || result.success) {
                // Generate cleanup summary message
                const cleanup = (result as any).cleanup_summary;
                let cleanupDetails: string[] = [];

                if (cleanup && cleanup.chat_sessions_deleted > 0) {
                    cleanupDetails.push(`${cleanup.chat_sessions_deleted} chat session(s)`);
                }
                if (cleanup && cleanup.chat_logs_deleted > 0) {
                    cleanupDetails.push(`${cleanup.chat_logs_deleted} chat log(s)`);
                }
                if (cleanup && cleanup.chat_history_deleted > 0) {
                    cleanupDetails.push(`chat history`);
                }
                if (cleanup && cleanup.notifications_deleted > 0) {
                    cleanupDetails.push(`${cleanup.notifications_deleted} notification(s)`);
                }

                const baseMessage = "User deleted successfully!";
                const cleanupMessage = cleanupDetails.length > 0
                    ? `${baseMessage} Cleaned up: ${cleanupDetails.join(', ')}.`
                    : baseMessage;

                toast.success(cleanupMessage);
                onDeleted?.(user.id);
                onClose();
                return;
            }

            // Handle manager with reassigned workers
            if (result.reassigned_count && result.reassigned_count > 0) {
                // Generate cleanup summary message
                const cleanup = (result as any).cleanup_summary;
                let cleanupDetails: string[] = [];

                if (cleanup && cleanup.chat_sessions_deleted > 0) {
                    cleanupDetails.push(`${cleanup.chat_sessions_deleted} chat session(s)`);
                }
                if (cleanup && cleanup.chat_logs_deleted > 0) {
                    cleanupDetails.push(`${cleanup.chat_logs_deleted} chat log(s)`);
                }

                const baseMessage = `Manager deleted successfully. ${result.reassigned_count} user(s) reassigned to ${result.replacement_manager?.name || 'the new manager'}.`;
                const cleanupMessage = cleanupDetails.length > 0
                    ? `${baseMessage} Cleaned up: ${cleanupDetails.join(', ')}.`
                    : baseMessage;

                toast.success(cleanupMessage);
                onDeleted?.(user.id);
                onClose();
                return;
            }

            // If we get here, we need replacement manager but the API didn't return 400 error
            // This shouldn't happen, but handle gracefully
            toast.error("Something went wrong. Please try again.");

        } catch (error: any) {
            if (error.message && (error.message.includes("Cannot delete. Please add another manager first") || error.message.includes("Cannot delete. Please create another manager first"))) {
                // Only one manager exists and has workers - block deletion
                toast.error("Cannot delete. Please add another manager first to reassign users.");
                onClose();
            } else if (error.message && error.message.includes("This manager has assigned workers. Please select a replacement manager to reassign them")) {
                // Manager has workers and multiple managers exist - show replacement modal
                onClose(); // Close this modal
                onShowReplaceManagerModal?.(user); // Open replacement modal
            } else {
                const errorMessage = error?.message || "Failed to delete user. Please try again.";
                toast.error(errorMessage);
            }
        } finally {
            setIsDeleting(false);
            setIsValidating(false);
        }
    };

    return (
        <Modal show={true} onHide={onClose} className='mini-modal-width' centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Delete User?</Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <div className="text-left">
                    <p className="text-gray-600 mb-6">Are you sure you want to delete this User?</p>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-3">
                        <button
                            onClick={onClose}
                            className="renegade-btn-secondary mini-btn"
                        >
                            No, Keep
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={isDeleting || isValidating}
                            className="renegade-btn-primary mini-btn"
                        >
                            {isValidating ? 'Validating...' : isDeleting ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

// User Info Modal Component
function UserInfoModal({ user, onClose, onEditUser }: { user: User; onClose: () => void; onEditUser: (user: User) => void }) {
    return (
        <Modal show={true} onHide={onClose} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">
                    User Info
                    <button
                        className="p-2 rounded-lg hover:bg-gray-100 ms-3"
                        onClick={() => onEditUser(user)}
                    >
                        <img src="/icons/editSOP.png" alt="Edit" className="w-4 h-4" />
                    </button>
                </Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                {/* Content */}
                <div>
                    {/* User Name */}
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-0">User Name</label>
                        <p className="text-14-reg grey-600">{user.name}</p>
                    </div>

                    {/* User Email */}
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-0">User Email</label>
                        <p className="text-14-reg grey-600">{user.email}</p>
                    </div>

                    {/* User Role */}
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-2">User Role</label><br />
                        <span className="inline-block px-3 py-1 text-sm bg-gray-100 text-gray-800 rounded-md">
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                    </div>

                    {/* Assigned Manager */}
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-0">Assigned Manager</label>
                        <p className="text-14-reg grey-600">
                            {user.role === 'manager' ? '—' : (user.assignedManager && user.assignedManager !== '—' ? user.assignedManager : '—')}
                        </p>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

// Replace Manager Modal Component
function ReplaceManagerModal({
    managerToDelete,
    onClose,
    onSuccess
}: {
    managerToDelete: User;
    onClose: () => void;
    onSuccess: (message: string) => void;
}) {
    const [selectedReplacementManager, setSelectedReplacementManager] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [managers, setManagers] = useState<ApiAdminUser[]>([]);
    const [managersLoading, setManagersLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [assignedWorkersCount, setAssignedWorkersCount] = useState<number>(0);

    // Fetch available managers and worker count when modal opens
    useEffect(() => {
        const loadData = async () => {
            try {
                setManagersLoading(true);
                setError("");

                // Fetch managers and workers in parallel
                const [managerRes, allUsersRes] = await Promise.all([
                    apiFetchUsers({ page: 1, pageSize: 100, role: 'manager', status: 'active' }),
                    apiFetchUsers({ page: 1, pageSize: 1000 }) // Get all users to count assigned workers
                ]);


                const availableManagers = managerRes.items.filter(manager => manager.id !== managerToDelete.id);
                setManagers(availableManagers);

                // Count workers assigned to this manager
                const assignedWorkers = allUsersRes.items.filter(user =>
                    user.role === 'worker' && user.assignedManager === managerToDelete.id
                );
                setAssignedWorkersCount(assignedWorkers.length);

                // If no other managers available, show error
                if (availableManagers.length === 0) {
                    setError('No other managers available for reassignment. Please create another manager first.');
                }

            } catch (error) {
                logger.error('Error loading manager data:', error);
                setError('Failed to load required data');
            } finally {
                setManagersLoading(false);
            }
        };
        loadData();
    }, [managerToDelete.id]);

    const handleSubmit = async () => {
        if (!selectedReplacementManager) {
            setError('Please select a replacement manager');
            return;
        }

        setIsProcessing(true);
        setError("");

        try {
            const result = await deleteUser(managerToDelete.id, selectedReplacementManager);

            if (result.reassigned_count && result.reassigned_count > 0) {
                // Generate cleanup summary message
                const cleanup = (result as any).cleanup_summary;
                let cleanupDetails: string[] = [];

                if (cleanup?.chat_sessions_deleted && cleanup.chat_sessions_deleted > 0) {
                    cleanupDetails.push(`${cleanup.chat_sessions_deleted} chat session(s)`);
                }
                if (cleanup?.chat_logs_deleted && cleanup.chat_logs_deleted > 0) {
                    cleanupDetails.push(`${cleanup.chat_logs_deleted} chat log(s)`);
                }

                const baseMessage = `Manager deleted successfully. ${result.reassigned_count} user(s) reassigned to ${result.replacement_manager?.name || 'the new manager'}.`;
                const cleanupMessage = cleanupDetails.length > 0
                    ? `${baseMessage} Cleaned up: ${cleanupDetails.join(', ')}.`
                    : baseMessage;

                onSuccess(cleanupMessage);
            } else {
                // Generate cleanup summary message
                const cleanup = (result as any).cleanup_summary;
                let cleanupDetails: string[] = [];

                if (cleanup?.chat_sessions_deleted && cleanup.chat_sessions_deleted > 0) {
                    cleanupDetails.push(`${cleanup.chat_sessions_deleted} chat session(s)`);
                }
                if (cleanup?.chat_logs_deleted && cleanup.chat_logs_deleted > 0) {
                    cleanupDetails.push(`${cleanup.chat_logs_deleted} chat log(s)`);
                }

                const baseMessage = 'Manager deleted successfully.';
                const cleanupMessage = cleanupDetails.length > 0
                    ? `${baseMessage} Cleaned up: ${cleanupDetails.join(', ')}.`
                    : baseMessage;

                onSuccess(cleanupMessage);
            }

            onClose();
        } catch (error: any) {
            const errorMessage = error?.message || "Failed to delete manager. Please try again.";
            setError(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Modal show={true} onHide={onClose} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-red-600">Delete Manager</Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <div className="mb-4">
                    <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <div>
                            <h5 className="text-lg font-semibold text-gray-900 mb-1">Confirm Manager Deletion</h5>
                            <p className="text-sm text-gray-600">
                                You are about to delete <strong>{managerToDelete.name}</strong>
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                        <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <h6 className="text-sm font-medium text-red-800 mb-1">Important!</h6>
                            <p className="text-sm text-red-700">
                                {assignedWorkersCount > 0
                                    ? `This manager has ${assignedWorkersCount} worker(s) assigned. All workers must be reassigned to another manager before deletion.`
                                    : "This manager will be deleted. Select a replacement manager to proceed."
                                }
                            </p>
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="replacementManager">Select Replacement Manager <span className="text-red-500">*</span></label>
                    <select
                        id="replacementManager"
                        value={selectedReplacementManager}
                        onChange={(e) => setSelectedReplacementManager(e.target.value)}
                        className="form-control"
                        disabled={managersLoading}
                    >
                        <option value="">Select a manager to reassign users to</option>
                        {managersLoading ? (
                            <option value="" disabled>Loading managers...</option>
                        ) : (managers.length === 0 && !managersLoading) ? (
                            <option value="" disabled>
                                {error.includes('No other managers')
                                    ? 'No other managers available for reassignment'
                                    : 'No replacement managers available'
                                }
                            </option>
                        ) : (
                            managers.map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                    {manager.name} ({manager.email})
                                </option>
                            ))
                        )}
                    </select>
                </div>

                {error && (
                    <div className="alert alert-danger mt-3" role="alert">
                        {error}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-end gap-3 px-4 py-3 border-t border-gray-200">
                <button
                    onClick={onClose}
                    className="renegade-btn-secondary mini-btn"
                    disabled={isProcessing}
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isProcessing || !selectedReplacementManager || managers.length === 0}
                    className={`mini-btn ${selectedReplacementManager ? 'renegade-btn-primary' : 'renegade-btn-secondary'}`}
                >
                    {isProcessing ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Deleting...
                        </>
                    ) : (
                        'Delete Manager'
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default AdminUsers;

